import { Injectable, Type } from '@angular/core';
import { Dictionary } from '../../../../type_definitions/vendor';
import { IGAFormTypeValue } from '../../../main/forms/forms.service';
import { BaseReportActionsHandler } from './base-report-actions-handler';
import { IObject } from '../../../../type_definitions/datatypes';
import { AddOrderCommentActionComponent } from './components/add-order-comment-action.component';
import { AddOemActionComponent } from './components/add-oem-action.component';
import { UpdateEolActionComponent } from './components/update-eol-action.component';
import { AddStatusActionComponent } from './components/add-status-action.component';
import { StatusPreconditionService } from './preconditions/status-precondition.service';
import { ChangeInvoiceRateActionComponent } from './components/change-invoice-rate-action.component';
import { ChangePaymentDateActionComponent } from './components/change-payment-date-action.component';
import { ChangePaymentStatusActionComponent } from './components/change-payment-status-action.component';
import { CopyInvoiceActionComponent } from './components/copy-invoice-action.component';
import { CloseOrderPendingActionComponent } from './components/close-order-pending-action.component';
import { MarkTransferredActionComponent } from './components/mark-transferred-action.component';
import { ChangeGbdmActionComponent } from './components/change-gbdm-action.component';
import { ChangeOdActionComponent } from './components/change-od-action.component';
import { ChangeWebshopContactActionComponent } from './components/change-webshop-contact-action.component';
import { SendSignupLinkActionComponent } from './components/send-signup-link-action.component';
import { AddImagesActionComponent } from './components/add-images-action.component';
import { AssignGenericUserActionComponent } from './components/assign-generic-user-action.component';
import { GenericUserPreconditionService } from './preconditions/generic-user-precondition.service';
import { PermissionsPreconditionService } from './preconditions/permissions-precondition.service';
import { PermissionsActionComponent } from './components/permissions-action.component';
import { MailFaqHandlerService } from './handlers/mail-faq-handler.service';
import { UploadOrderDocumentActionComponent } from './components/upload-order-document-action.component';
import { SendMailHandlerService } from './handlers/send-mail-handler.service';
import { MailInvoiceHandlerService } from './handlers/mail-invoice-handler.service';
import { OutgoingFeeInvoiceActionComponent } from './components/outgoing-fee-invoice-action.component';
import { MainPendingActionComponent } from './components/main-pending-action.component';
import { MainPendingCheckedActionComponent } from './components/main-pending-checked-action.component';
import { ChangeInvoiceMailAddressActionComponent } from './components/change-invoice-mail-address-action.component';
import { ChangeBpActionComponent } from './components/change-bp-action.component';
import { ChangeBpPreconditionService } from './preconditions/change-bp-precondition.service';
import { DownloadBpHandlerService } from './handlers/download-bp-handler.service';
import { BatchBillingActionComponent } from './components/batch-billing-action.component';
import { BpCatalogSelectionHandlerService } from './handlers/bp-catalog-selection-handler.service';
import { Ga3FormHandlerService } from './handlers/ga3-form-handler.service';
import { ChangeInvoiceTemplateActionComponent } from './components/change-invoice-template-action.component';
import { SetDefaultOEMLocationContactActionComponent } from './components/set-default-oem-location-contact-action.component';
import { SetOEMRankingActionComponent } from './components/set-oem-ranking-action.component';
import { SetOEMProcessingShippingModeActionComponent } from './components/set-oem-processing-shipping-mode-action.component';
import { OEMTierStatusPreconditionService } from './preconditions/oem-tier-status-precondition.service';
import { SendInvoiceHandlerService } from './handlers/send-invoice-handler.service';
import { ChangeSendingStatusActionComponent } from './components/change-sending-status-action.component';
import { MailNewWebshopActionComponent } from './components/mail-new-webshop-action.component';
import { DialogSize } from '../../message/ga-message.service';
import { UpdateDocumentTypeActionComponent } from './components/update-document-type-action.component';
import { MailPriceConfirmationHandlerService } from './handlers/mail-price-confirmation-handler.service';

export interface IGA3ReportAction {
  formId: string;
  context?: Dictionary<IGAFormTypeValue>;
  key: string;
  init?: string;
  reportId?: number;
}

export interface IReportActionParamDialogOptions {
  reloadRows?:boolean;
  component?: Type<any>;
  dialogSize?:DialogSize;
  actionDialogParams?: IObject;
  //name of a function that needs to be executed before showing the dialog
  //the parameters will be reportId, reportAction, rowSelection
  //when returning an error message, the action will not be executed
  precondition?: Type<BaseReportActionsHandler>;
  handler?: Type<BaseReportActionsHandler>;
  standalone?:boolean;
}

export interface IReportActionParamDialogRegistry {
  [ index:string ]:IReportActionParamDialogOptions;
}

@Injectable()
export class ReportActionsConfigService {
  private actions: IReportActionParamDialogRegistry = {
    ADD_OEM: {
      component: AddOemActionComponent
    },
    UPDATE_EOL: {
      component: UpdateEolActionComponent
    },
    ADD_ORDER_COMMENT: {
      component: AddOrderCommentActionComponent
    },
    ADD_PAYMENT_COMMENT: {
      component: AddOrderCommentActionComponent,
      actionDialogParams: {
        hideWorkflow: true
      }
    },
    ADD_STATUS: {
      component: AddStatusActionComponent,
      precondition: StatusPreconditionService
    },
    CREATE_INVOICE_WITH_NEW_RATES: {
      component: ChangeInvoiceRateActionComponent
    },
    CHANGE_PAYMENT_DATE: {
      component: ChangePaymentDateActionComponent
    },
    CHANGE_PAYMENT_STATUS: {
      component: ChangePaymentStatusActionComponent
    },
    COPY_INVOICE: {
      component: CopyInvoiceActionComponent
    },
    CLOSE_ORDER_PENDING: {
      component: CloseOrderPendingActionComponent,
      precondition: StatusPreconditionService
    },
    MARK_TRANSFERRED: {
      component: MarkTransferredActionComponent
    },
    CHANGE_GBDM_DEPT: {
      component: ChangeGbdmActionComponent
    },
    CHANGE_OD_COUNTRIES: {
      component: ChangeOdActionComponent
    },
    CHANGE_OD_BP: {
      component: ChangeOdActionComponent
    },
    CHANGE_CONTACT: {
      component: ChangeWebshopContactActionComponent
    },
    SEND_SIGNUP_LINK: {
      component: SendSignupLinkActionComponent
    },
    ADD_IMAGES: {
      component: AddImagesActionComponent
    },
    ASSIGN_GENERIC_USER: {
      component: AssignGenericUserActionComponent,
      precondition: GenericUserPreconditionService
    },
    // -- customer permissions
    ADD_WEBSHOP_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      actionDialogParams: {
        showAccessType: true
      },
      dialogSize: DialogSize.LG
    },
    REMOVE_WEBSHOP_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    ADD_COUNTRIES_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    REMOVE_COUNTRIES_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    ADD_REGIONS_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    REMOVE_REGIONS_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    ADD_SUBCOMPANY_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    REMOVE_SUBCOMPANY_CUSTOMER_PERMI: {
      component: PermissionsActionComponent,
      precondition: PermissionsPreconditionService,
      dialogSize: DialogSize.LG
    },
    // -- end customer permissions
    MAIL_FAQ: {
      handler: MailFaqHandlerService
    },
    UPLOAD_ORDER_DOCUMENT: {
      component: UploadOrderDocumentActionComponent
    },
    SEND_MAIL: {
      handler: SendMailHandlerService
    },
    SEND_LOCAL_INVOICE: {
      handler: MailInvoiceHandlerService
    },
    CREATE_OUTGOINGFEE_INVOICE: {
      component: OutgoingFeeInvoiceActionComponent
    },
    SET_MAIN_PENDING: {
      component: MainPendingActionComponent
    },
    SET_MAIN_PENDING_CHECKED: {
      component: MainPendingCheckedActionComponent
    },
    CHANGE_INVOICE_MAIL_ADDRESS: {
      component: ChangeInvoiceMailAddressActionComponent
    },
    CHANGE_BPS: {
      component: ChangeBpActionComponent,
      precondition: ChangeBpPreconditionService,
      reloadRows: true
    },
    DOWNLOAD_BP_PROFILE: {
      handler: DownloadBpHandlerService
    },
    ADD_TO_BATCH_BILL: {
      component: BatchBillingActionComponent
    },
    SEND_BATCH_BILL: {
      handler: MailInvoiceHandlerService
    },
    SEND_PROFORMA_BATCH_BILL: {
      handler: MailInvoiceHandlerService
    },
    SEND_INVOICE: {
      handler: SendInvoiceHandlerService
    },
    MARK_DEFAULT_ROLE: {
      // this means params will not be getting from component or handler but straight to execute report action REST api (only with report row data in api request body)
      reloadRows: true
    },
    CHANGE_BPCATALOG_SELECTION: {
      handler: BpCatalogSelectionHandlerService
    },
    SET_DEFAULT_OEM_LOCATION_CONTACT: {
      component: SetDefaultOEMLocationContactActionComponent
    },
    SET_OEM_RANKING: {
      component: SetOEMRankingActionComponent
    },
    SET_OEM_PROCESSING_SHIPPING_MODE: {
      component: SetOEMProcessingShippingModeActionComponent,
      precondition: OEMTierStatusPreconditionService
    },
    GA3_FORM_ACTION: {
      handler: Ga3FormHandlerService,
      reloadRows: true,
      standalone: true
    },
    CHANGE_INVOICE_TEMPLATE: {
      component: ChangeInvoiceTemplateActionComponent
    },
    CHANGE_SENDING_STATUS: {
      component: ChangeSendingStatusActionComponent
    },
    MAIL_NEW_WEBSHOP: {
      component: MailNewWebshopActionComponent
    },
    UPDATE_DOCUMENT_TYPE: {
      component: UpdateDocumentTypeActionComponent
    },
    SEND_PRICE_CONF_NOTIF: {
      handler: MailPriceConfirmationHandlerService
    },
    SEND_CATAL_PRICE_CONF_NOTIF:  {
      handler: MailPriceConfirmationHandlerService
    }
  };

  public getConfig(actionCode:string):IReportActionParamDialogOptions {
    return this.actions[ actionCode ];
  }
}